// src/components/financials/ExpenseTrendAnalysis.js
// Updated: 04/06/2026 - Fixed Monthly Average calculation to only count active months

import React, { useState, useEffect } from 'react';
import { 
  Box, Paper, Typography, Grid, TextField, MenuItem, 
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  CircularProgress, Tooltip
} from '@mui/material';
import { TrendingUp } from '@mui/icons-material';
import { db } from '../../services/firebase';
import { collection, getDocs, query } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

const ExpenseTrendAnalysis = () => {
  const { userProfile } = useAuth();
  const churchId = userProfile?.churchId;

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [matrix, setMatrix] = useState({});
  const [categories, setCategories] = useState([]);
  const [totals, setTotals] = useState(Array(12).fill(0));
  const [loading, setLoading] = useState(false);
  
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  useEffect(() => {
    if (!churchId) return;

    const fetchData = async () => {
      setLoading(true);

      try {
        const colsToCheck = ['transactions', 'expenses'];
        let allDocs = [];

        for (const colName of colsToCheck) {
            const colRef = collection(db, 'churches', churchId, 'financials', 'data', colName);
            const q = query(colRef); 
            const snap = await getDocs(q);
            if (!snap.empty) {
                snap.docs.forEach(d => allDocs.push({ ...d.data(), _id: d.id }));
            }
        }

        const tempMatrix = {};
        const tempTotals = Array(12).fill(0);

        allDocs.forEach(t => {
            if (!t.date) return;
            const itemDate = new Date(t.date);
            if (isNaN(itemDate.getTime())) return; 
            
            const dateStr = t.date.toString();
            if (!dateStr.startsWith(year.toString())) {
                if (itemDate.getFullYear() !== parseInt(year)) return;
            }

            const type = (t.type || t.transactionType || '').toLowerCase();
            const incomeTypes = [
              'contribution', 'deposit', 'opening_balance', 'transfer_in', 
              'income', 'revenue', 'donation', 'tithe', 'offering'
            ];
            
            if (incomeTypes.includes(type) || t.donorId || t.donorName || t.entryType === 'credit') return;
            if (type === 'transfer' || type === 'transfer_out') return;

            const monthIdx = new Date(t.date + 'T12:00:00').getMonth(); 
            
            const addToMatrix = (catName, amt) => {
                if (catName === "Donation" || catName === "Tithe" || catName === "Offering") return;
                
                const safeCat = catName || "General Operating"; 
                if (!tempMatrix[safeCat]) tempMatrix[safeCat] = Array(12).fill(0);
                
                const absAmount = Math.abs(parseFloat(amt) || 0);
                tempMatrix[safeCat][monthIdx] += absAmount;
                tempTotals[monthIdx] += absAmount;
            };

            if (t.splits && Array.isArray(t.splits) && t.splits.length > 0) {
                t.splits.forEach(split => addToMatrix(split.category, split.amount));
            } else {
                addToMatrix(t.category, t.amount);
            }
        });

        setMatrix(tempMatrix);
        setCategories(Object.keys(tempMatrix).sort());
        setTotals(tempTotals);

      } catch (e) {
        console.error("Error fetching trends:", e);
      }
      setLoading(false);
    };

    fetchData();
  }, [churchId, year]);

  const getStyleForCell = (val, rowAvg) => {
      if (val === 0) return { color: '#ccc' };
      if (val > rowAvg * 1.5 && val > 100) return { bgcolor: '#ffebee', fontWeight: 'bold', color: '#d32f2f' }; 
      return {};
  };

  return (
    <Box>
        <Paper sx={{ p: 3, mb: 3 }}>
            <Grid container alignItems="center" spacing={2}>
                <Grid item>
                    <TrendingUp color="secondary" sx={{ fontSize: 40 }} />
                </Grid>
                <Grid item xs>
                    <Typography variant="h6">Monthly Expense Trend Analysis</Typography>
                    <Typography variant="body2" color="textSecondary">
                        Tracks all spending month-over-month. Cells highlighted in <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>red</span> indicate spending significantly higher than your monthly average.
                    </Typography>
                </Grid>
                <Grid item>
                    <TextField 
                        select 
                        label="Fiscal Year" 
                        value={year} 
                        onChange={(e) => setYear(e.target.value)}
                        size="small"
                        sx={{ width: 150 }}
                    >
                        {[0,1,2,3].map(i => (
                            <MenuItem key={i} value={currentYear - i}>{currentYear - i}</MenuItem>
                        ))}
                    </TextField>
                </Grid>
            </Grid>
        </Paper>

        {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
        ) : (
            <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', minWidth: 150 }}>Category</TableCell>
                            {months.map(m => (
                                <TableCell key={m} align="right" sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>{m}</TableCell>
                            ))}
                            <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#e0e0e0', borderLeft: '2px solid #ccc' }}>Total</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#e0e0e0' }}>Avg/Mo</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {categories.length > 0 ? categories.map(cat => {
                            const rowData = matrix[cat];
                            const rowTotal = rowData.reduce((a, b) => a + b, 0);
                            
                            // --- NEW LOGIC: Calculate active months for true average ---
                            const activeMonthsCount = rowData.filter(amount => amount > 0).length;
                            const divisor = activeMonthsCount > 0 ? activeMonthsCount : 1; 
                            const rowAvg = rowTotal / divisor;

                            return (
                                <TableRow key={cat} hover>
                                    <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>
                                        {cat}
                                    </TableCell>
                                    {rowData.map((amt, idx) => (
                                        <Tooltip key={idx} title={amt > rowAvg * 1.5 ? `High! Avg is ${rowAvg.toFixed(0)}` : ""} arrow>
                                            <TableCell 
                                                align="right" 
                                                sx={getStyleForCell(amt, rowAvg)}
                                            >
                                                {amt !== 0 ? amt.toLocaleString(undefined, { maximumFractionDigits: 0 }) : "-"}
                                            </TableCell>
                                        </Tooltip>
                                    ))}
                                    <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#f9f9f9', borderLeft: '2px solid #ccc' }}>
                                        {rowTotal.toLocaleString()}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontStyle: 'italic', color: 'text.secondary', bgcolor: '#f9f9f9' }}>
                                        {rowAvg.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                    </TableCell>
                                </TableRow>
                            );
                        }) : (
                            <TableRow>
                                <TableCell colSpan={14} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                                    No expenses found for {year}.
                                </TableCell>
                            </TableRow>
                        )}

                        {categories.length > 0 && (
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', borderTop: '2px solid #000' }}>TOTAL EXPENSES</TableCell>
                                {totals.map((t, i) => (
                                    <TableCell key={i} align="right" sx={{ fontWeight: 'bold', fontSize: '1rem', borderTop: '2px solid #000' }}>
                                        {t > 0 ? t.toLocaleString(undefined, { maximumFractionDigits: 0 }) : "-"}
                                    </TableCell>
                                ))}
                                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem', borderTop: '2px solid #000', bgcolor: '#eee' }}>
                                    {totals.reduce((a,b)=>a+b,0).toLocaleString()}
                                </TableCell>
                                <TableCell />
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        )}
    </Box>
  );
};

export default ExpenseTrendAnalysis;